import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import classNames from 'classnames'
import { isEmpty } from 'lodash'
import queryString from 'query-string'
import {
  IconCheck,
  IconFilter,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconSearch,
  IconVolume,
} from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { TextArea } from '@/components/forms/TextArea'
import { TextInput } from '@/components/forms/TextInput'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { FilterTypeMultiSelect } from '@/components/filters/types/FilterTypeMultiSelect'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Loader } from '@/components/loaders/Loader'
import { fetchVoices, saveVoiceSelection } from '@/reducers/voiceAgents/voiceAgents.actions'
import { setChosenVoice } from '@/reducers/voiceAgents/voiceAgents.redux'

import {
  GENDER_OPTIONS,
  VOICE_PAGE_SIZE,
  VOICE_SAY_MAX_LENGTH,
  filterVoices,
  genderLabel,
  languageLabel,
  languageOptionsFromVoices,
} from '../voiceHelpers'
import { useVoiceAudio } from '../hooks/useVoiceAudio'
import { voiceMissingLanguages } from '../languageHelpers'
import EmptyState from './EmptyState'

const DEFAULT_PHRASE = 'Hi, thanks for calling. How can I help you today?'

// The gradient avatar cycles through the theme's gradient tokens
// (--gradient-0 … --gradient-20 in variables.scss).
const GRADIENT_COUNT = 21

// "Configure Agent Voice" rail (Overview tab): browse the white-labeled voice
// catalog, play free canned previews, hear a custom phrase with the selected
// voice (metered — explicit click only), and pick the agent's voice. Save
// persists via PATCH /prompts/voices; the saved voice preselects on load
// through the catalog response's selectedVoiceId.
const VoicePanel = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const agentId = useSelector((state) => state.voiceAgents.selectedAgentId)
  const voices = useSelector((state) => state.voiceAgents.voices)
  const voicesLoading = useSelector((state) => Boolean(state.voiceAgents.loading.voices))
  const voicesError = useSelector((state) => state.voiceAgents.error.voices)
  const sayLoading = useSelector((state) => Boolean(state.voiceAgents.loading.sayVoice))
  const saveLoading = useSelector((state) => Boolean(state.voiceAgents.loading.saveVoice))
  const sayError = useSelector((state) => state.voiceAgents.error.sayVoice)
  const { chosenId, savedId } = useSelector((state) => state.voiceAgents.voiceSelection)
  // The configured language set, loaded by the Languages panel on the same
  // tab. Until it lands (or when the agent has none) nothing is gated and the
  // BE's 400 on save is the backstop.
  const configuredLanguages = useSelector(
    (state) => state.voiceAgents.languageSettings?.languages ?? []
  )

  // Admin org scope threaded in from the list page (`?org=`), passed through
  // as requested_organization_id like the other Agent Studio calls.
  const { org } = queryString.parse(location.search)

  const [phrase, setPhrase] = useState(DEFAULT_PHRASE)
  const [search, setSearch] = useState('')
  const [genders, setGenders] = useState([])
  const [languages, setLanguages] = useState([])
  const [visibleCount, setVisibleCount] = useState(VOICE_PAGE_SIZE)

  const { playingId, previewLoadingId, togglePreview, sayPhrase } = useVoiceAudio({
    agentId,
    orgId: org,
  })

  // Load once per agent: the guards make re-runs no-ops (already loaded, in
  // flight, or errored — errors only refetch via the explicit Try again).
  useEffect(() => {
    if (agentId && voices.agentId !== agentId && !voicesLoading && !voicesError) {
      dispatch(fetchVoices(agentId, org))
    }
  }, [dispatch, agentId, voices.agentId, voicesLoading, voicesError, org])

  const languageOptions = languageOptionsFromVoices(voices.items)
  const filtered = filterVoices(voices.items, { search, genders, languages })
  const shown = filtered.slice(0, visibleCount)
  const remaining = filtered.length - visibleCount
  const anyFilter = Boolean(search.trim()) || !isEmpty(genders) || !isEmpty(languages)
  const dirty = chosenId !== savedId
  const trimmedPhrase = phrase.trim()

  // Any filter change collapses the list back to the first page.
  const updateFilter = (apply) => {
    apply()
    setVisibleCount(VOICE_PAGE_SIZE)
  }

  const renderCatalog = () => {
    if (voicesLoading) {
      return <Loader centered label="Loading voices…" />
    }
    if (voicesError?.kind === 'retryable') {
      return (
        <div className="voice-agents__voice-error">
          <ErrorMessage content="Couldn't load voices." />
          <Button small onClick={() => dispatch(fetchVoices(agentId, org))}>
            Try again
          </Button>
        </div>
      )
    }
    if (voicesError) {
      return (
        <EmptyState
          icon={IconVolume}
          title="Voices aren't available for this agent"
          hint={
            voicesError.kind === 'notConfigured'
              ? 'This agent isn’t set up for voice previews yet.'
              : 'No voice catalog was found for this agent.'
          }
          dataTestId="voice-catalog-unavailable"
        />
      )
    }
    if (isEmpty(filtered)) {
      return (
        <EmptyState
          icon={IconSearch}
          title="No voices match your filters"
          hint="Try removing a filter or clearing your search."
          dataTestId="voice-catalog-no-results"
        />
      )
    }
    return (
      <>
        <div role="radiogroup" aria-label="Voices" className="voice-agents__voice-list">
          {shown.map((voice) => {
            const isChosen = voice.voiceId === chosenId
            const gradientIndex = voices.items.indexOf(voice) % GRADIENT_COUNT
            const isPlaying = playingId === voice.voiceId
            const missing = voiceMissingLanguages(voice, configuredLanguages)
            const isSelectable = isEmpty(missing)
            // `languages` is the full spoken set; older BE payloads only carry
            // the native `language`. The row's primary tag stays `language`,
            // the field the Language filter keys off — `languages` is derived
            // from `accents[]` native-first, which only matches `language` when
            // an accent is actually flagged native, so tagging from it would
            // let a row display a language its own filter bucket disagrees with.
            const spoken = Array.isArray(voice.languages) ? voice.languages : [voice.language]
            const alsoSpeaks = spoken.filter((code) => code !== voice.language)
            const selectVoice = () => {
              if (isSelectable) {
                dispatch(setChosenVoice(voice.voiceId))
              }
            }
            return (
              <div
                key={voice.voiceId}
                role="radio"
                aria-checked={isChosen}
                aria-disabled={!isSelectable || undefined}
                tabIndex={0}
                className={classNames('voice-agents__voice-row', {
                  // The highlight ring marks a *pending* (unsaved) pick — a
                  // saved choice shows only the filled radio, per the design.
                  'voice-agents__voice-row--chosen': isChosen && chosenId !== savedId,
                  'voice-agents__voice-row--disabled': !isSelectable,
                })}
                onClick={selectVoice}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    selectVoice()
                  }
                }}
                data-testid={`voice-row-${voice.voiceId}`}
              >
                {voice.previewUrl ? (
                  <button
                    type="button"
                    className={classNames(
                      'voice-agents__voice-blob',
                      `voice-agents__voice-blob--g${gradientIndex}`
                    )}
                    aria-label={isPlaying ? 'Pause preview' : 'Play preview'}
                    onClick={(event) => {
                      event.stopPropagation()
                      togglePreview(voice)
                    }}
                    data-testid={`voice-preview-${voice.voiceId}`}
                  >
                    {previewLoadingId === voice.voiceId && <Loader size="small" />}
                    {previewLoadingId !== voice.voiceId && isPlaying && (
                      <IconPlayerPauseFilled size={12} />
                    )}
                    {previewLoadingId !== voice.voiceId && !isPlaying && (
                      <IconPlayerPlayFilled size={12} />
                    )}
                  </button>
                ) : (
                  <span
                    aria-hidden="true"
                    className={classNames(
                      'voice-agents__voice-blob',
                      'voice-agents__voice-blob--static',
                      `voice-agents__voice-blob--g${gradientIndex}`
                    )}
                  />
                )}
                <div className="voice-agents__voice-info">
                  <div className="voice-agents__voice-name">
                    {voice.name}
                    <span className="voice-agents__voice-tags">
                      {[genderLabel(voice.gender), languageLabel(voice.language)]
                        .filter(Boolean)
                        .join(' · ')}
                      {!isEmpty(alsoSpeaks) && (
                        <InteractiveTooltip content={alsoSpeaks.map(languageLabel).join(', ')}>
                          <span className="voice-agents__voice-more-langs">
                            +{alsoSpeaks.length}
                          </span>
                        </InteractiveTooltip>
                      )}
                    </span>
                  </div>
                  {voice.description && (
                    <div className="voice-agents__voice-desc">{voice.description}</div>
                  )}
                  {!isSelectable && (
                    <div
                      className="voice-agents__voice-missing"
                      data-testid={`voice-missing-${voice.voiceId}`}
                    >
                      No {missing.map(languageLabel).join(', ')}
                    </div>
                  )}
                </div>
                <span
                  className={classNames('voice-agents__voice-radio', {
                    'voice-agents__voice-radio--checked': isChosen,
                  })}
                  aria-hidden="true"
                >
                  {isChosen && <IconCheck size={12} />}
                </span>
              </div>
            )
          })}
        </div>
        {(remaining > 0 || visibleCount > VOICE_PAGE_SIZE) && (
          <div className="voice-agents__voice-load-row">
            {remaining > 0 && (
              <Button
                basic
                small
                onClick={() => setVisibleCount(visibleCount + VOICE_PAGE_SIZE)}
                data-testid="voice-load-more"
              >
                Load more ({remaining})
              </Button>
            )}
            {remaining <= 0 && (
              <Button basic small onClick={() => setVisibleCount(VOICE_PAGE_SIZE)}>
                Show less
              </Button>
            )}
          </div>
        )}
      </>
    )
  }

  return (
    <aside className="voice-agents__voice-panel" aria-label="Configure Agent Voice">
      <div className="voice-agents__voice-panel-header">
        <h3 className="voice-agents__voice-panel-title">Configure Agent Voice</h3>
        {dirty && (
          <div className="voice-agents__voice-panel-actions">
            <Button
              small
              disabled={saveLoading}
              onClick={() => dispatch(setChosenVoice(savedId))}
              data-testid="voice-cancel"
            >
              Cancel
            </Button>
            <Button
              brand
              small
              loading={saveLoading}
              disabled={saveLoading}
              onClick={() => dispatch(saveVoiceSelection(agentId, chosenId, org))}
              data-testid="voice-save"
            >
              Save
            </Button>
          </div>
        )}
      </div>
      <div className="voice-agents__voice-panel-body">
        <div className="voice-agents__voice-field">
          <label htmlFor="voice-say-phrase" className="voice-agents__voice-label">
            Preview phrase
          </label>
          <TextArea
            id="voice-say-phrase"
            className="voice-agents__voice-say-input"
            spellCheck={false}
            maxLength={VOICE_SAY_MAX_LENGTH}
            value={phrase}
            onChange={(event) => setPhrase(event.target.value)}
          />
          <div className="voice-agents__voice-say-row">
            <span className="voice-agents__voice-say-counter">
              {phrase.length}/{VOICE_SAY_MAX_LENGTH}
            </span>
            <Button
              small
              loading={sayLoading}
              disabled={!chosenId || !trimmedPhrase || sayLoading}
              disabledTooltip={!chosenId ? 'Select a voice first' : undefined}
              onClick={() => sayPhrase(chosenId, trimmedPhrase)}
              data-testid="voice-say-button"
            >
              <IconVolume /> Say it
            </Button>
          </div>
          <p className="voice-agents__voice-say-hint">
            Plays your phrase with the selected voice — audio is generated fresh on every click.
          </p>
          {sayError && <ErrorMessage content={sayError.detail} />}
        </div>

        <div className="voice-agents__voice-field">
          <label htmlFor="voice-search" className="voice-agents__voice-label">
            Find a voice
          </label>
          <div className="voice-agents__voice-search">
            <IconSearch size={16} />
            <TextInput
              id="voice-search"
              autoComplete="off"
              placeholder="Search by name or style…"
              value={search}
              onChange={(event) => updateFilter(() => setSearch(event.target.value))}
              data-testid="voice-search-input"
            />
          </div>
        </div>

        <div className="voice-agents__voice-filters">
          <span className="voice-agents__voice-filters-label">
            <IconFilter size={15} /> Filter
          </span>
          <FilterTypeMultiSelect
            schema={{
              accessor: 'genders',
              label: 'Gender',
              options: GENDER_OPTIONS,
              applyOnChange: true,
            }}
            value={GENDER_OPTIONS.filter((option) => genders.includes(option.value))}
            updateSearch={(accessor, selected) =>
              updateFilter(() => setGenders((selected || []).map((option) => option.value)))
            }
          />
          <FilterTypeMultiSelect
            schema={{
              accessor: 'languages',
              label: 'Language',
              options: languageOptions,
              applyOnChange: true,
            }}
            value={languageOptions.filter((option) => languages.includes(option.value))}
            updateSearch={(accessor, selected) =>
              updateFilter(() => setLanguages((selected || []).map((option) => option.value)))
            }
          />
        </div>

        <div className="voice-agents__voice-count-row">
          <span className="voice-agents__voice-count" data-testid="voice-count">
            {isEmpty(filtered)
              ? '0 voices'
              : `Showing ${shown.length} of ${filtered.length} voice${
                  filtered.length === 1 ? '' : 's'
                }`}
          </span>
          {anyFilter && (
            <Button
              text
              small
              onClick={() =>
                updateFilter(() => {
                  setSearch('')
                  setGenders([])
                  setLanguages([])
                })
              }
            >
              Clear filters
            </Button>
          )}
        </div>

        {renderCatalog()}
      </div>
    </aside>
  )
}

export default VoicePanel
